<template>
  <div class="lg:w-1/2 px-16">
    <h3 class="text-h4">{{ sectionTitle }}</h3>
    <template v-for="(card, index) in visibleCards">
      <h3 v-if="card.isSectionHeader" :key="`header-${index}`" class="text-grey-header text-overline mt-32 mb-16">
        {{ card.title }}
      </h3>
      <ImageLinkCard
        v-else
        :key="`card-${index}`"
        :class="card.className"
        :title="card.title"
        :content="card.content"
        :elevation="card.elevation"
        :link-u-r-l="card.linkURL"
        :use-full-page-navigation="card.useFullPageNavigation"
        :clickable="card.clickable"
      >
        <template #rightSide>
          <DisclosureIcon class="mr-0 ml-16" />
        </template>
      </ImageLinkCard>
    </template>
  </div>
</template>

<script lang="ts">
import ImageLinkCard from '@/components/image_link_card/ImageLinkCard.vue';
import DisclosureIcon from '@/graphics/icons/DisclosureIcon.vue';

interface ActionCard {
  title: string;
  content?: string;
  elevation?: number;
  linkURL?: string;
  useFullPageNavigation?: boolean;
  clickable?: boolean;
  className?: string;
  condition?: () => boolean;
  isSectionHeader?: boolean;
}

export default {
  name: 'ProfileCompleteActions',
  components: {
    ImageLinkCard,
    DisclosureIcon,
  },
  props: {
    isAdditionalProfile: {
      type: Boolean,
      required: true,
    },
    hasPhoto: {
      type: Boolean,
      required: true,
    },
    formattedCareType: {
      type: String,
      required: true,
    },
    jobSearchLink: {
      type: String,
      required: true,
    },
    profileEditLink: {
      type: String,
      required: true,
    },
    shouldUseSpaRoutes: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    sectionTitle(): string {
      return this.isAdditionalProfile ? 'Take these steps to get hired faster' : 'Take the first step to land a job';
    },
    addProfilePhotoCard(): ActionCard {
      return {
        title: 'Add a profile photo',
        content: 'Putting a face to your names helps build trust with families',
        elevation: 2,
        linkURL: '/sitter/photo_upload#/sitter_upload',
        useFullPageNavigation: true,
        clickable: true,
        className: 'mt-16',
        condition: () => !this.hasPhoto,
      };
    },
    standardProfileCards(): ActionCard[] {
      return [
        this.addProfilePhotoCard,
        {
          isSectionHeader: true,
          title: 'Other ways to get started',
        },
        {
          title: 'Apply to 10 or more jobs',
          content: 'Sitters who apply to 10 or more jobs are 4x more likely to get hired',
          elevation: 1,
          linkURL: this.jobSearchLink,
          useFullPageNavigation: true,
          clickable: true,
          className: 'mt-16',
        },
        {
          title: 'Make yourself stand out',
          content: 'Personalize your profile with your education, references, and more',
          elevation: 1,
          linkURL: this.profileEditLink,
          useFullPageNavigation: !this.shouldUseSpaRoutes,
          clickable: true,
          className: 'mt-16',
        },
      ];
    },
    additionalProfileCards(): ActionCard[] {
      return [
        this.addProfilePhotoCard,
        {
          title: `Start applying to ${this.formattedCareType} jobs`,
          content: 'Apply to at least 5 jobs to increase your visibility with families looking for this service',
          elevation: 2,
          linkURL: this.jobSearchLink,
          useFullPageNavigation: true,
          clickable: true,
          className: 'mt-16',
        },
        {
          title: 'Highlight your relevant experience',
          content: 'Customize your profile with skills and experience specific to this care type to attract more families',
          elevation: 1,
          linkURL: this.profileEditLink,
          useFullPageNavigation: !this.shouldUseSpaRoutes,
          clickable: true,
          className: 'mt-16',
        },
      ];
    },
    visibleCards(): ActionCard[] {
      const cards = this.isAdditionalProfile ? this.additionalProfileCards : this.standardProfileCards;
      return cards.filter((card) => {
        if (card.isSectionHeader) return true;
        return !card.condition || card.condition();
      });
    },
  },
};
</script>
